import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { can } from '@racedash/shared'
import { useShell } from '../AppShell'
import OrgNav from '../components/OrgNav'
import {
  createCar,
  createDriver,
  deleteCar,
  deleteDriver,
  updateCar,
  updateDriver,
  useCars,
  useDrivers,
} from '../team/api'
import type { Car, Driver } from '../team/api'

/**
 * The cars an organisation runs and the people who drive them.
 *
 * Both exist so that things downstream can refer to them rather than repeat
 * them: an entry names a car, and declaring who is in the car is a pick from
 * this list. A typo'd driver name silently splits one person's stints into two
 * in every later report, and nothing can tell afterwards that it happened.
 */
export default function Team() {
  const { currentOrg, role } = useShell()
  const { items: cars, error: carsError } = useCars(currentOrg?.id)
  const { items: drivers, error: driversError } = useDrivers(currentOrg?.id)

  const [editingCar, setEditingCar] = useState<Car | null>(null)
  const [addingCar, setAddingCar] = useState(false)
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null)
  const [addingDriver, setAddingDriver] = useState(false)

  if (!currentOrg) return <Text c="dimmed">Organisation not found.</Text>
  const mayEdit = can(role, 'dashboard.edit')

  return (
    <Stack gap="lg">
      <div>
        <Title order={1} size="h2">
          Team
        </Title>
        <Text c="dimmed" size="sm">
          Your cars and drivers. Entering a car at an event, and saying who is in it during a
          session, both pick from these.
        </Text>
      </div>

      <OrgNav orgId={currentOrg.id} role={role} />

      {(carsError || driversError) && <Alert color="red">{carsError ?? driversError}</Alert>}

      <Paper withBorder p="lg">
        <Group justify="space-between" align="flex-start" mb="md">
          <div>
            <Title order={2} size="h5">
              Cars
            </Title>
            <Text c="dimmed" size="sm">
              The number is what appears on the timing screen. It can be changed for a single event
              when you enter.
            </Text>
          </div>
          {mayEdit && <Button onClick={() => setAddingCar(true)}>New car</Button>}
        </Group>

        {cars && cars.length > 0 ? (
          <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={100}>Number</Table.Th>
                <Table.Th>Label</Table.Th>
                <Table.Th w={150} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {cars.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Text size="sm" fw={500} ff="monospace">
                      {c.number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {c.label || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td align="right">
                    {mayEdit && (
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Button variant="subtle" size="compact-sm" onClick={() => setEditingCar(c)}>
                          Edit
                        </Button>
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-sm"
                          onClick={() => void deleteCar(currentOrg.id, c.id)}
                        >
                          Remove
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed" size="sm">
            No cars yet. Add one before entering an event.
          </Text>
        )}
      </Paper>

      <Paper withBorder p="lg">
        <Group justify="space-between" align="flex-start" mb="md">
          <div>
            <Title order={2} size="h5">
              Drivers
            </Title>
            <Text c="dimmed" size="sm">
              Everyone who might drive. The short name is for the places a full one will not fit.
            </Text>
          </div>
          {mayEdit && <Button onClick={() => setAddingDriver(true)}>New driver</Button>}
        </Group>

        {drivers && drivers.length > 0 ? (
          <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th w={140}>Short</Table.Th>
                <Table.Th w={150} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {drivers.map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {d.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" ff="monospace">
                      {d.shortName || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td align="right">
                    {mayEdit && (
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => setEditingDriver(d)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-sm"
                          onClick={() => void deleteDriver(currentOrg.id, d.id)}
                        >
                          Remove
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed" size="sm">
            No drivers yet.
          </Text>
        )}
      </Paper>

      <TwoFieldDialog
        opened={addingCar || editingCar !== null}
        onClose={() => {
          setAddingCar(false)
          setEditingCar(null)
        }}
        title={editingCar ? `Edit car ${editingCar.number}` : 'New car'}
        firstLabel="Number"
        firstPlaceholder="436"
        firstRequired
        secondLabel="Label"
        secondPlaceholder="The blue one"
        initialFirst={editingCar?.number ?? ''}
        initialSecond={editingCar?.label ?? ''}
        onSubmit={(number, label) =>
          editingCar
            ? updateCar(currentOrg.id, editingCar.id, number, label)
            : createCar(currentOrg.id, number, label)
        }
      />

      <TwoFieldDialog
        opened={addingDriver || editingDriver !== null}
        onClose={() => {
          setAddingDriver(false)
          setEditingDriver(null)
        }}
        title={editingDriver ? `Edit ${editingDriver.name}` : 'New driver'}
        firstLabel="Name"
        firstPlaceholder="Alex Marsh"
        firstRequired
        secondLabel="Short name"
        secondPlaceholder="MAR"
        initialFirst={editingDriver?.name ?? ''}
        initialSecond={editingDriver?.shortName ?? ''}
        onSubmit={(name, short) =>
          editingDriver
            ? updateDriver(currentOrg.id, editingDriver.id, name, short)
            : createDriver(currentOrg.id, name, short)
        }
      />
    </Stack>
  )
}

/**
 * Both records here are a required field and an optional one, so they share a
 * dialog. Two nearly-identical forms would drift, and the difference between
 * them is entirely in the labels.
 */
function TwoFieldDialog({
  opened,
  onClose,
  title,
  firstLabel,
  firstPlaceholder,
  firstRequired,
  secondLabel,
  secondPlaceholder,
  initialFirst,
  initialSecond,
  onSubmit,
}: {
  opened: boolean
  onClose: () => void
  title: string
  firstLabel: string
  firstPlaceholder: string
  firstRequired?: boolean
  secondLabel: string
  secondPlaceholder: string
  initialFirst: string
  initialSecond: string
  onSubmit: (first: string, second: string) => Promise<unknown>
}) {
  const [first, setFirst] = useState('')
  const [second, setSecond] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setFirst(initialFirst)
    setSecond(initialSecond)
    setFailed(null)
  }, [opened, initialFirst, initialSecond])

  const changed = first.trim() !== initialFirst.trim() || second.trim() !== initialSecond.trim()
  const canSubmit = (!firstRequired || Boolean(first.trim())) && changed

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !canSubmit) return
    setBusy(true)
    setFailed(null)
    try {
      await onSubmit(first, second)
      onClose()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal opened={opened} onClose={() => !busy && onClose()} title={title}>
      <form onSubmit={(e) => void submit(e)}>
        <Stack gap="md">
          {failed && <Alert color="red">{failed}</Alert>}
          <TextInput
            label={firstLabel}
            value={first}
            onChange={(e) => setFirst(e.currentTarget.value)}
            placeholder={firstPlaceholder}
            data-autofocus
            required={firstRequired}
          />
          <TextInput
            label={secondLabel}
            value={second}
            onChange={(e) => setSecond(e.currentTarget.value)}
            placeholder={secondPlaceholder}
          />
          <Group justify="flex-end">
            <Button variant="default" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!canSubmit}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
